import React from 'react';
import styled from '@emotion/styled';
import { easing, palette, zIndex } from '../../style-utils/theme';
import { LOOP } from '../../style-utils/motion';

interface SpinnerProps {
  isAddNewPropertyForm?: boolean;
  isId?: boolean;
}

export const Spinner: React.FC<SpinnerProps> = ({
  isAddNewPropertyForm,
  isId,
}) => {
  // NOT aria-hidden, unlike every other icon in this folder. A spinner is the
  // only thing on screen saying the app is working, so it is announced rather
  // than skipped — and as a status, which waits for a gap instead of
  // interrupting whatever is being read.
  return (
    <SpinnerSvg
      role="status"
      aria-label="Working"
      width="65px"
      height="65px"
      viewBox="0 0 66 66"
      xmlns="http://www.w3.org/2000/svg"
      isAddNewPropertyForm={isAddNewPropertyForm}
      isId={isId}
    >
      <SpinnerPath
        className="path"
        fill="none"
        strokeWidth="6"
        strokeLinecap="round"
        cx="33"
        cy="33"
        r="24"
      ></SpinnerPath>
    </SpinnerSvg>
  );
};

const SpinnerSvg = styled.svg<{
  isAddNewPropertyForm?: boolean;
  isId?: boolean;
}>`
  ${({ isId }) =>
    isId
      ? `
  position:relative;
  margin:  auto;
      `
      : `
  position: absolute;
  top: 40%;
  left: 40%;
  `}

  /* zIndex.toast, not 999999 (WP-18). design_system.md:379-382 names this
     exact number as the thing not to write: a literal that large is not a
     layer, it is an attempt to win an argument with a scale it is not part
     of. The spinner does belong above everything, and toast is that tier.
     LEFT IN PLACE by the motion pass: the absolute-positioned form sits over
     form content that has its own stacking (overlay spinner forms), so the
     layer is load-bearing there, not cosmetic. */
  z-index: ${zIndex.toast};
  /* LOOP.spinner, not a literal: the one ambient-loop scale in
     style-utils/motion.ts, so this spinner and the shimmer keep one tempo. */
  animation: rotator ${LOOP.spinner} linear infinite;

  @keyframes rotator {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(270deg);
    }
  }
`;

const SpinnerPath = styled.circle`
  stroke-dasharray: 187;
  stroke-dashoffset: 0;
  transform-origin: center;
  animation: dash ${LOOP.spinner} ${easing.standard} infinite,
    colors ${LOOP.spinnerHue} ${easing.standard} infinite;

  @keyframes colors {
    0% {
      stroke: ${palette.iris};
    }
    25% {
      stroke: ${palette.irisGlow};
    }
    50% {
      stroke: ${palette.irisDark};
    }
    75% {
      stroke: ${palette.irisGlow};
    }
    100% {
      stroke: ${palette.iris};
    }
  }
  @keyframes dash {
    0% {
      stroke-dashoffset: 187;
    }
    50% {
      stroke-dashoffset: 46.75;
      transform: rotate(135deg);
    }
    100% {
      stroke-dashoffset: 187;
      transform: rotate(450deg);
    }
  }
`;
